/**
 * Visão dos tokens (`docs/spec/scene-walls.md`), em funções puras e em células:
 * - o polígono de visão a partir do centro do token, por raios até as pontas das paredes;
 * - os cones (alcance e ângulo), a luz (ambiente e dos tokens) e a combinação do original:
 *   vê-se o alcance de luz intensa onde há luz intensa, o de luz fraca onde há luz fraca e o de
 *   escuro em qualquer lugar;
 * - as zonas da névoa (sempre visível, sempre oculta, parcialmente visível, à explorar);
 * - os caminhos SVG para desenhar tudo isso.
 */
import type { FogRect, SceneToken } from './model.js';
import { segmentsOf, wallJoints, type Wall, type WallDir, type WallSight } from './walls.js';

/** Polígono: pares x, y em células. */
export type Poly = number[];
export type Bounds = { x: number; y: number; w: number; h: number };

/** Segmento que pode parar a visão, a luz ou o movimento. Paredes abertas (portas) não entram. */
export type SightSeg = { ax: number; ay: number; bx: number; by: number; sight: WallSight; dir: WallDir; move: boolean; wall: string | null };

export function sightSegments(walls: Wall[]): SightSeg[] {
  const out: SightSeg[] = [];
  const shut = walls.filter((wall) => !wall.open);
  for (const wall of shut) {
    for (const [ax, ay, bx, by] of segmentsOf(wall)) out.push({ ax, ay, bx, by, sight: wall.sight, dir: wall.dir, move: wall.move, wall: wall.id });
  }
  // as emendas das frestas entre pontas que quase se tocam (BRAZ-19): bloqueiam como a mais permissiva
  // das duas paredes. Com mão única não há lado certo para a emenda, e ela fica de fora
  for (const j of wallJoints(shut)) {
    if (j.from.dir !== 'both' || j.to.dir !== 'both') continue;
    const sight = SIGHT_STRENGTH[j.from.sight] <= SIGHT_STRENGTH[j.to.sight] ? j.from.sight : j.to.sight;
    const move = j.from.move && j.to.move;
    if (sight === 'none' && !move) continue;
    out.push({ ax: j.ax, ay: j.ay, bx: j.bx, by: j.by, sight, dir: 'both', move, wall: j.from.id });
  }
  return out;
}

/** Quanto cada visão de parede bloqueia (a emenda fica com a menor das duas). */
const SIGHT_STRENGTH: Record<WallSight, number> = { none: 0, limited: 1, normal: 2 };

/** De que lado do segmento (indo de a para b, com o y para baixo) está o ponto. */
export function sideOf(s: Pick<SightSeg, 'ax' | 'ay' | 'bx' | 'by'>, x: number, y: number): 'left' | 'right' | null {
  const cross = (s.bx - s.ax) * (y - s.ay) - (s.by - s.ay) * (x - s.ax);
  return cross < 0 ? 'left' : cross > 0 ? 'right' : null;
}

/** Parede de mão única bloqueia só quem está do lado dela. */
export const blocksFrom = (s: SightSeg, x: number, y: number): boolean => s.dir === 'both' || sideOf(s, x, y) === s.dir;

const TAU = Math.PI * 2;
const EPS = 1e-4;
/** Amostras do círculo do alcance (o arco vira cordas de 3,75°). */
const ARC_SAMPLES = 96;

/** Raios lançados de um ponto: ângulo e distância até onde cada um para. */
export type Cast = { ox: number; oy: number; cone: boolean; angles: number[]; dists: number[]; hits: (string | null)[] };

function distToSeg(px: number, py: number, s: SightSeg): number {
  const dx = s.bx - s.ax;
  const dy = s.by - s.ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - s.ax) * dx + (py - s.ay) * dy) / len2)) : 0;
  return Math.hypot(px - (s.ax + t * dx), py - (s.ay + t * dy));
}

/** Distância ao longo do raio (dx, dy unitário) até o segmento, ou -1. */
function rayHit(ox: number, oy: number, dx: number, dy: number, s: SightSeg): number {
  const ex = s.bx - s.ax;
  const ey = s.by - s.ay;
  const den = dx * ey - dy * ex;
  if (Math.abs(den) < 1e-12) return -1;
  const wx = s.ax - ox;
  const wy = s.ay - oy;
  const t = (wx * ey - wy * ex) / den;
  const u = (wx * dy - wy * dx) / den;
  return t > 1e-9 && u >= -1e-9 && u <= 1 + 1e-9 ? t : -1;
}

/** Ângulos em que o segmento cruza o círculo de raio r em volta de (ox, oy). */
function circleCrossings(ox: number, oy: number, r: number, s: SightSeg, out: number[]): void {
  const dx = s.bx - s.ax;
  const dy = s.by - s.ay;
  const fx = s.ax - ox;
  const fy = s.ay - oy;
  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - r * r;
  const disc = b * b - 4 * a * c;
  if (a === 0 || disc < 0) return;
  const sq = Math.sqrt(disc);
  for (const t of [(-b - sq) / (2 * a), (-b + sq) / (2 * a)]) {
    if (t < 0 || t > 1) continue;
    out.push(Math.atan2(fy + t * dy, fx + t * dx));
  }
}

/**
 * Lança os raios de (ox, oy). Para em parede `normal`; numa `limited` (terreno) passa a primeira e
 * para na segunda; `none` (janela) não para. As bordas da cena sempre param.
 * - `ranges`: alcances que vão ser recortados depois (os arcos ganham raios próprios);
 * - `facing` (graus, sentido do relógio a partir do norte) e `angle` (graus): o cone.
 */
export function castRays(ox: number, oy: number, segs: SightSeg[], bounds: Bounds, opts: { ranges: number[]; facing?: number; angle?: number }): Cast {
  const maxRange = Math.max(0, ...opts.ranges.filter((r) => Number.isFinite(r)));
  const edges: SightSeg[] = [
    [bounds.x, bounds.y, bounds.x + bounds.w, bounds.y],
    [bounds.x + bounds.w, bounds.y, bounds.x + bounds.w, bounds.y + bounds.h],
    [bounds.x + bounds.w, bounds.y + bounds.h, bounds.x, bounds.y + bounds.h],
    [bounds.x, bounds.y + bounds.h, bounds.x, bounds.y],
  ].map(([ax, ay, bx, by]) => ({ ax: ax!, ay: ay!, bx: bx!, by: by!, sight: 'normal', dir: 'both', move: true, wall: null }));
  const near = segs.filter((s) => s.sight !== 'none' && blocksFrom(s, ox, oy) && (!maxRange || distToSeg(ox, oy, s) <= maxRange + EPS));
  const all = [...near, ...edges];

  const angles: number[] = [];
  for (const s of all) {
    for (const [px, py] of [
      [s.ax, s.ay],
      [s.bx, s.by],
    ] as const) {
      const a = Math.atan2(py - oy, px - ox);
      angles.push(a - EPS, a, a + EPS);
    }
  }
  for (const r of opts.ranges) {
    if (!(r > 0) || !Number.isFinite(r)) continue;
    for (let i = 0; i < ARC_SAMPLES; i++) angles.push((i / ARC_SAMPLES) * TAU - Math.PI);
    for (const s of near) circleCrossings(ox, oy, r, s, angles);
  }

  // cone: ângulos relativos ao começo do cone, de 0 a `width`
  const width = Math.min(360, Math.max(10, opts.angle ?? 360));
  const cone = width < 360;
  const start = cone ? (((opts.facing ?? 0) - width / 2) * Math.PI) / 180 - Math.PI / 2 : -Math.PI;
  const span = cone ? (width * Math.PI) / 180 : TAU;
  const rel = (a: number) => (((a - start) % TAU) + TAU) % TAU;
  const list = angles.map(rel).filter((a) => a <= span);
  if (cone) list.push(0, span);
  list.sort((p, q) => p - q);
  const unique: number[] = [];
  for (const a of list) if (!unique.length || a - unique[unique.length - 1]! > 1e-7) unique.push(a);

  const out: Cast = { ox, oy, cone, angles: [], dists: [], hits: [] };
  const hits: [number, SightSeg][] = [];
  for (const relA of unique) {
    const a = start + relA;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    hits.length = 0;
    for (const s of all) {
      const t = rayHit(ox, oy, dx, dy, s);
      if (t > 0) hits.push([t, s]);
    }
    hits.sort((p, q) => p[0] - q[0]);
    let dist = Infinity;
    let hit: string | null = null;
    let limited = 0;
    for (let i = 0; i < hits.length; ) {
      // os cruzamentos no mesmo ponto (a quina de dois segmentos) contam uma vez só, e param o raio se
      // algum deles parar: o terreno encostado na quina de uma parede normal não deixa a visão passar
      const t = hits[i]![0];
      let stop: SightSeg | null = null;
      let first: SightSeg | null = null;
      for (; i < hits.length && hits[i]![0] - t < 1e-7; i++) {
        const s = hits[i]![1];
        first ??= s;
        if (s.sight !== 'limited') stop ??= s;
      }
      if (!stop) {
        limited += 1;
        if (limited < 2) continue;
        stop = first;
      }
      dist = t;
      hit = stop!.wall;
      break;
    }
    out.angles.push(a);
    out.dists.push(dist);
    out.hits.push(hit);
  }
  return out;
}

/** O polígono dos raios recortado no alcance `range`, e a parede em que cada ponto parou. */
export function polygonOf(cast: Cast, range: number): { points: Poly; hits: (string | null)[] } {
  const points: Poly = [];
  const hits: (string | null)[] = [];
  if (cast.cone) {
    points.push(cast.ox, cast.oy);
    hits.push(null);
  }
  for (let i = 0; i < cast.angles.length; i++) {
    const raw = cast.dists[i]!;
    const d = Math.min(raw, range, 1e5);
    const x = cast.ox + Math.cos(cast.angles[i]!) * d;
    const y = cast.oy + Math.sin(cast.angles[i]!) * d;
    const n = points.length;
    if (n >= 2 && Math.abs(points[n - 2]! - x) < 1e-6 && Math.abs(points[n - 1]! - y) < 1e-6) continue;
    points.push(x, y);
    hits.push(raw <= range ? cast.hits[i]! : null);
  }
  return { points, hits };
}

export function pointInPolygon(poly: Poly, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const xi = poly[i]!;
    const yi = poly[i + 1]!;
    const xj = poly[j]!;
    const yj = poly[j + 1]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Os trechos de parede em que o polígono encosta (a "faixa" que o jogador vê). */
export function wallHits(poly: { points: Poly; hits: (string | null)[] }): number[][] {
  const out: number[][] = [];
  const n = poly.hits.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const id = poly.hits[i];
    if (id && id === poly.hits[j]) out.push([poly.points[i * 2]!, poly.points[i * 2 + 1]!, poly.points[j * 2]!, poly.points[j * 2 + 1]!]);
  }
  return out;
}

/* ------------------------------------------------------------------ visão e luz */

export type AmbientLight = 'intense' | 'weak' | 'none';
export type FogSharing = 'individual' | 'group';

export type Shape = { points: Poly; hits: (string | null)[] };
/** O que um token pode ver (cones cortados pelas paredes), por alcance. */
export type TokenSightShapes = { id: string; bright: Shape | null; dim: Shape | null; dark: Shape | null };
export type LightShapes = { id: string; bright: Shape | null; dim: Shape | null };

/** Unidades da cena (metros) → células. */
export const toCells = (units: number, cellDistance: number): number => (cellDistance > 0 ? units / cellDistance : units);

export const tokenCenter = (t: Pick<SceneToken, 'x' | 'y' | 'size'>): { x: number; y: number } => ({ x: t.x + t.size / 2, y: t.y + t.size / 2 });

/** As três visões possíveis do token (alcances em células; 0 é não ver naquela luz). */
export function tokenSight(id: string, x: number, y: number, ranges: { bright: number; dim: number; dark: number }, segs: SightSeg[], bounds: Bounds, cone?: { facing: number; angle: number }): TokenSightShapes {
  const list = [ranges.bright, ranges.dim, ranges.dark].filter((r) => r > 0);
  if (!list.length) return { id, bright: null, dim: null, dark: null };
  const cast = castRays(x, y, segs, bounds, { ranges: list, ...(cone ?? {}) });
  const shape = (r: number) => (r > 0 ? polygonOf(cast, r) : null);
  return { id, bright: shape(ranges.bright), dim: shape(ranges.dim), dark: shape(ranges.dark) };
}

export function lightShapes(id: string, x: number, y: number, ranges: { bright: number; dim: number }, segs: SightSeg[], bounds: Bounds, cone?: { facing: number; angle: number }): LightShapes {
  const s = tokenSight(id, x, y, { ...ranges, dark: 0 }, segs, bounds, cone);
  return { id, bright: s.bright, dim: s.dim };
}

/** Tudo o que decide o que uma pessoa vê agora. */
export type SightState = { ambient: AmbientLight; tokens: TokenSightShapes[]; lights: LightShapes[] };

const inShape = (s: Shape | null, x: number, y: number) => !!s && pointInPolygon(s.points, x, y);
export const litBright = (st: SightState, x: number, y: number): boolean => st.ambient === 'intense' || st.lights.some((l) => inShape(l.bright, x, y));
export const litDim = (st: SightState, x: number, y: number): boolean => st.ambient === 'weak' || st.lights.some((l) => inShape(l.dim, x, y));

/** O que se vê agora no ponto (sem as zonas). */
export function seesNow(st: SightState, x: number, y: number): boolean {
  let bright: boolean | undefined;
  let dim: boolean | undefined;
  for (const t of st.tokens) {
    if (inShape(t.dark, x, y)) return true;
    if (inShape(t.bright, x, y) && (bright ??= litBright(st, x, y))) return true;
    if (inShape(t.dim, x, y) && (dim ??= litDim(st, x, y))) return true;
  }
  return false;
}

/** Visto mal (esmaecido): dentro do alcance de luz intensa, mas só com luz fraca. */
export const seesDimly = (st: SightState, x: number, y: number): boolean => st.tokens.some((t) => inShape(t.bright, x, y)) && litDim(st, x, y);

/* ------------------------------------------------------------------ quem dá visão */

export type Viewer = { kind: 'player'; userId: string } | { kind: 'all' } | { kind: 'spectator' };

/**
 * Os tokens que dão visão a quem olha (conferido no original, etapa 0):
 * - jogador: os dele, sempre; na visão em grupo, todos os que têm quem os controle;
 * - "todos os jogadores" (o mestre olhando como jogador): os que têm quem os controle e os NPCs
 *   com "possui visão";
 * - espectador: todos os que têm quem os controle.
 * Tokens ocultos, da camada do mestre ou só de luz não contam.
 */
export function visionTokens(tokens: SceneToken[], viewer: Viewer, sharing: FogSharing): SceneToken[] {
  return tokens.filter((t) => {
    if (t.hidden || t.layer === 'gm' || t.lightOnly) return false;
    const owned = t.controllers.length > 0;
    if (viewer.kind === 'player') return t.controllers.includes(viewer.userId) || (sharing === 'group' && owned);
    if (viewer.kind === 'all') return owned || t.sight.has;
    return owned;
  });
}

/** Os tokens que iluminam a cena (os ocultos e os da camada do mestre não). */
export const lightTokens = (tokens: SceneToken[]): SceneToken[] => tokens.filter((t) => !t.hidden && t.layer !== 'gm' && (t.sight.lightBright > 0 || t.sight.lightDim > 0));

/* ------------------------------------------------------------------ movimento */

/** Raio do círculo que anda com o token (como no original: 15% do lado do token). */
export const moveRadius = (size: number): number => size * 0.15;

/**
 * Anda de `from` até `to` (centros, em células) com um círculo de raio `radius`; para encostado na
 * primeira parede que bloqueia o movimento. Paredes abertas já não estão em `segs`; a de mão única
 * só bloqueia quem vem do lado dela.
 */
export function moveWithWalls(from: { x: number; y: number }, to: { x: number; y: number }, radius: number, segs: SightSeg[]): { x: number; y: number; blocked: boolean; wall?: SightSeg } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return { ...to, blocked: false };
  let first = Infinity;
  let wall: SightSeg | undefined;
  for (const s of segs) {
    if (!s.move || !blocksFrom(s, from.x, from.y)) continue;
    const t = contactTime(from.x, from.y, dx, dy, radius, s);
    if (t < first) {
      first = t;
      wall = s;
    }
  }
  if (first > 1) return { ...to, blocked: false };
  // um fio antes do contato, para não ficar encostado por dentro
  const t = Math.max(0, first - 1e-3 / len);
  return { x: from.x + dx * t, y: from.y + dy * t, blocked: true, wall };
}

/**
 * Como `moveWithWalls`, mas, ao encostar, o resto do movimento continua ao longo da parede (some só
 * a parte que empurra contra ela) e, passada a ponta dela, segue de novo para o destino. Assim o
 * token arrastado contorna a parede acompanhando o mouse, em vez de ficar parado nela. Tenta
 * `tries` vezes, e para quando não anda mais.
 */
export function slideWithWalls(from: { x: number; y: number }, to: { x: number; y: number }, radius: number, segs: SightSeg[], tries = 4): { x: number; y: number; blocked: boolean } {
  let at = { x: from.x, y: from.y };
  for (let k = 0; k < tries; k++) {
    const hit = moveWithWalls(at, to, radius, segs);
    if (!hit.blocked) return { x: hit.x, y: hit.y, blocked: k > 0 };
    const next = hit.wall ? slideAlong(hit, hit.wall, to, radius, segs) : hit;
    if (Math.hypot(next.x - at.x, next.y - at.y) < 1e-6) break;
    at = next;
  }
  return { ...at, blocked: true };
}

/** Do contato `hit` com a parede `s`, anda o resto do caminho até `to` ao longo dela. */
function slideAlong(hit: { x: number; y: number }, s: SightSeg, to: { x: number; y: number }, radius: number, segs: SightSeg[]): { x: number; y: number } {
  // a normal da parede no contato: do ponto dela mais perto até o centro do token
  const ex = s.bx - s.ax;
  const ey = s.by - s.ay;
  const len2 = ex * ex + ey * ey;
  const u = len2 ? Math.max(0, Math.min(1, ((hit.x - s.ax) * ex + (hit.y - s.ay) * ey) / len2)) : 0;
  const nx0 = hit.x - (s.ax + u * ex);
  const ny0 = hit.y - (s.ay + u * ey);
  const nl = Math.hypot(nx0, ny0);
  if (nl < 1e-9) return hit;
  const nx = nx0 / nl;
  const ny = ny0 / nl;
  const rx = to.x - hit.x;
  const ry = to.y - hit.y;
  const push = rx * nx + ry * ny;
  if (push >= 0) return hit;
  const sx = rx - push * nx;
  const sy = ry - push * ny;
  if (Math.hypot(sx, sy) < 1e-6) return hit;
  const slid = moveWithWalls(hit, { x: hit.x + sx, y: hit.y + sy }, radius, segs);
  return { x: slid.x, y: slid.y };
}

/** Primeiro instante t (0..1+) em que o círculo, andando (dx, dy), encosta no segmento. */
function contactTime(px: number, py: number, dx: number, dy: number, r: number, s: SightSeg): number {
  const d0 = distToSeg(px, py, s);
  // já encostado, ou um fio dentro do raio (a posição gravada é arredondada): deixa sair e andar ao
  // longo da parede, não deixa chegar mais perto. Sem a folga acima do raio, quem ficava a menos de
  // 1e-9 dele pulava a reta do lado de cá e atravessava a parede
  if (d0 < r + 1e-6) {
    const d1 = distToSeg(px + dx * 1e-3, py + dy * 1e-3, s);
    return d1 < d0 - 1e-9 ? 0 : Infinity;
  }
  let best = Infinity;
  const ex = s.bx - s.ax;
  const ey = s.by - s.ay;
  const L = Math.hypot(ex, ey);
  if (L > 1e-12) {
    // as duas retas paralelas ao segmento, a `r` dele
    const nx = -ey / L;
    const ny = ex / L;
    const s0 = (px - s.ax) * nx + (py - s.ay) * ny;
    const ds = dx * nx + dy * ny;
    if (Math.abs(ds) > 1e-12) {
      for (const side of [r, -r]) {
        const t = (side - s0) / ds;
        if (t < 0 || t >= best) continue;
        const u = ((px + dx * t - s.ax) * ex + (py + dy * t - s.ay) * ey) / L;
        if (u >= 0 && u <= L) best = t;
      }
    }
  }
  // as pontas do segmento
  for (const [cx, cy] of [
    [s.ax, s.ay],
    [s.bx, s.by],
  ] as const) {
    const fx = px - cx;
    const fy = py - cy;
    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - r * r;
    const disc = b * b - 4 * a * c;
    if (disc < 0) continue;
    const t = (-b - Math.sqrt(disc)) / (2 * a);
    if (t >= 0 && t < best) best = t;
  }
  return best;
}

/* ------------------------------------------------------------------ zonas */

export const ZONE = { explore: 0, reveal: 1, hide: 2, semi: 3 } as const;
const ZONE_OF: Record<FogRect['mode'], number> = { explore: ZONE.explore, reveal: ZONE.reveal, hide: ZONE.hide, semi: ZONE.semi };

/** As zonas por célula do tabuleiro; o último retângulo que cobre a célula decide. */
export function zoneGrid(fog: FogRect[], cols: number, rows: number): Uint8Array {
  const grid = new Uint8Array(cols * rows);
  for (const f of fog) {
    const x1 = Math.max(0, Math.floor(f.x));
    const y1 = Math.max(0, Math.floor(f.y));
    const x2 = Math.min(cols, Math.ceil(f.x + f.w));
    const y2 = Math.min(rows, Math.ceil(f.y + f.h));
    const kind = ZONE_OF[f.mode];
    for (let y = y1; y < y2; y++) grid.fill(kind, y * cols + x1, y * cols + Math.max(x1, x2));
  }
  return grid;
}

export const zoneAt = (grid: Uint8Array, cols: number, rows: number, x: number, y: number): number => {
  const cx = Math.floor(x);
  const cy = Math.floor(y);
  return cx >= 0 && cy >= 0 && cx < cols && cy < rows ? grid[cy * cols + cx]! : ZONE.explore;
};

/** Faixas horizontais de células de um tipo (para desenhar). */
export function zoneRects(grid: Uint8Array, cols: number, rows: number, kind: number): Bounds[] {
  const out: Bounds[] = [];
  for (let y = 0; y < rows; y++) {
    let x = 0;
    while (x < cols) {
      if (grid[y * cols + x] !== kind) {
        x += 1;
        continue;
      }
      const start = x;
      while (x < cols && grid[y * cols + x] === kind) x += 1;
      out.push({ x: start, y, w: x - start, h: 1 });
    }
  }
  return out;
}

/* ------------------------------------------------------------------ memória */

/**
 * Memória do token (o que ele já viu): uma grade de meia célula, em blocos de 16 × 16 células.
 * Cada bloco é um atributo `t<bx>_<by>` do filho `memory`, com 32 × 32 bits em base64; andar só
 * regrava os blocos que mudaram. (O original guarda um polígono; os bits dispensam uma biblioteca
 * de polígonos e regravam pouco a cada passo.)
 */
export const MEM_RES = 2;
export const MEM_TILE = 16;
const TILE_SIDE = MEM_TILE * MEM_RES;
const TILE_BYTES = (TILE_SIDE * TILE_SIDE) / 8;

export const tileKey = (bx: number, by: number) => `t${bx}_${by}`;
const parseTileKey = (key: string): [number, number] | null => {
  const m = /^t(\d+)_(\d+)$/.exec(key);
  return m ? [Number(m[1]), Number(m[2])] : null;
};

export function encodeTile(bits: Uint8Array): string {
  let bin = '';
  for (const b of bits) bin += String.fromCharCode(b);
  return btoa(bin);
}

export function decodeTile(value: string | undefined): Uint8Array {
  const out = new Uint8Array(TILE_BYTES);
  if (!value) return out;
  try {
    const bin = atob(value);
    for (let i = 0; i < Math.min(bin.length, TILE_BYTES); i++) out[i] = bin.charCodeAt(i);
  } catch {
    // bloco estragado: começa vazio
  }
  return out;
}

/** As células de meia célula cujo centro está dentro dos polígonos, por bloco (só dentro do tabuleiro). */
export function rasterize(polys: Poly[], cols: number, rows: number): Map<string, Uint8Array> {
  const tiles = new Map<string, Uint8Array>();
  const W = cols * MEM_RES;
  const H = rows * MEM_RES;
  const xs: number[] = [];
  for (const poly of polys) {
    if (poly.length < 6) continue;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 1; i < poly.length; i += 2) {
      minY = Math.min(minY, poly[i]!);
      maxY = Math.max(maxY, poly[i]!);
    }
    const j0 = Math.max(0, Math.floor(minY * MEM_RES));
    const j1 = Math.min(H - 1, Math.ceil(maxY * MEM_RES));
    for (let j = j0; j <= j1; j++) {
      const y = (j + 0.5) / MEM_RES;
      xs.length = 0;
      for (let i = 0, k = poly.length - 2; i < poly.length; k = i, i += 2) {
        const yi = poly[i + 1]!;
        const yk = poly[k + 1]!;
        if (yi > y !== yk > y) xs.push(poly[i]! + ((y - yi) * (poly[k]! - poly[i]!)) / (yk - yi));
      }
      xs.sort((a, b) => a - b);
      for (let n = 0; n + 1 < xs.length; n += 2) {
        // colunas cujo centro (i + 0,5) / RES cai em [xs[n], xs[n+1]]
        const i0 = Math.max(0, Math.ceil(xs[n]! * MEM_RES - 0.5));
        const i1 = Math.min(W - 1, Math.floor(xs[n + 1]! * MEM_RES - 0.5));
        for (let i = i0; i <= i1; i++) {
          const key = tileKey(Math.floor(i / TILE_SIDE), Math.floor(j / TILE_SIDE));
          let bits = tiles.get(key);
          if (!bits) tiles.set(key, (bits = new Uint8Array(TILE_BYTES)));
          const bit = (j % TILE_SIDE) * TILE_SIDE + (i % TILE_SIDE);
          bits[bit >> 3]! |= 1 << (bit & 7);
        }
      }
    }
  }
  return tiles;
}

/** A luz em meias células, para cruzar com a visão na memória; `null` é o mapa inteiro (a luz ambiente). */
export type LitTiles = { bright: Map<string, Uint8Array> | null; dim: Map<string, Uint8Array> | null };

export function litTiles(ambient: AmbientLight, lights: LightShapes[], cols: number, rows: number): LitTiles {
  const of = (list: (Shape | null)[]) => rasterize(list.flatMap((s) => (s ? [s.points] : [])), cols, rows);
  return {
    bright: ambient === 'intense' ? null : of(lights.map((l) => l.bright)),
    dim: ambient === 'weak' ? null : of(lights.map((l) => l.dim)),
  };
}

/**
 * O que o token vê de fato, em meias células: o que a névoa deixa de cobrir (`VisionFog`). É o
 * alcance de escuro em qualquer lugar, o de luz intensa onde há luz (a fraca deixa ver esmaecido)
 * e o de luz fraca onde há luz fraca. É isso que entra na memória: no escuro, o alcance de luz
 * intensa sem luz não conta (BRAZ-12; o original guarda a visão possível inteira).
 */
export function seenTiles(shapes: TokenSightShapes, lit: LitTiles, cols: number, rows: number): Map<string, Uint8Array> {
  const out = shapes.dark ? rasterize([shapes.dark.points], cols, rows) : new Map<string, Uint8Array>();
  const add = (shape: Shape | null, lights: (Map<string, Uint8Array> | null)[]) => {
    if (!shape) return;
    const everywhere = lights.includes(null);
    for (const [key, bits] of rasterize([shape.points], cols, rows)) {
      const masks = everywhere ? [] : lights.map((l) => l?.get(key)).filter((m): m is Uint8Array => !!m);
      if (!everywhere && !masks.length) continue;
      let into = out.get(key);
      for (let i = 0; i < TILE_BYTES; i++) {
        let m = everywhere ? 0xff : 0;
        for (const mask of masks) m |= mask[i]!;
        const v = bits[i]! & m;
        if (!v) continue;
        if (!into) out.set(key, (into = new Uint8Array(TILE_BYTES)));
        into[i]! |= v;
      }
    }
  };
  add(shapes.bright, [lit.bright, lit.dim]);
  add(shapes.dim, [lit.dim]);
  return out;
}

/** Os blocos que mudam ao somar `seen` à memória atual (atributo → base64). */
export function mergeMemory(current: Record<string, string>, seen: Map<string, Uint8Array>): Record<string, string> {
  const changed: Record<string, string> = {};
  for (const [key, add] of seen) {
    const bits = decodeTile(current[key]);
    let grew = false;
    for (let i = 0; i < TILE_BYTES; i++) {
      const next = bits[i]! | add[i]!;
      if (next !== bits[i]) {
        bits[i] = next;
        grew = true;
      }
    }
    if (grew) changed[key] = encodeTile(bits);
  }
  return changed;
}

/** A soma das memórias como retângulos (faixas de meia célula por linha), em células. */
export function memoryRects(memories: Record<string, string>[]): Bounds[] {
  const union = new Map<string, Uint8Array>();
  for (const memory of memories) {
    for (const [key, value] of Object.entries(memory)) {
      if (!parseTileKey(key)) continue;
      const bits = decodeTile(value);
      const into = union.get(key);
      if (!into) union.set(key, bits);
      else for (let i = 0; i < TILE_BYTES; i++) into[i]! |= bits[i]!;
    }
  }
  const out: Bounds[] = [];
  for (const [key, bits] of union) {
    const [bx, by] = parseTileKey(key)!;
    for (let row = 0; row < TILE_SIDE; row++) {
      let col = 0;
      while (col < TILE_SIDE) {
        const bit = row * TILE_SIDE + col;
        if (!(bits[bit >> 3]! & (1 << (bit & 7)))) {
          col += 1;
          continue;
        }
        const start = col;
        while (col < TILE_SIDE) {
          const b = row * TILE_SIDE + col;
          if (!(bits[b >> 3]! & (1 << (b & 7)))) break;
          col += 1;
        }
        out.push({ x: (bx * TILE_SIDE + start) / MEM_RES, y: (by * TILE_SIDE + row) / MEM_RES, w: (col - start) / MEM_RES, h: 1 / MEM_RES });
      }
    }
  }
  return out;
}

/** Quantas meias células estão na memória (para conferir nos testes). */
export const memorySize = (rects: Bounds[]): number => rects.reduce((n, r) => n + Math.round(r.w * MEM_RES), 0);

/* ------------------------------------------------------------------ caminhos SVG */

const n2 = (v: number) => Math.round(v * 100) / 100;

/** Polígonos (em células) como um caminho só, em px; todos no mesmo sentido, a soma é a união. */
export function polysPath(polys: Poly[], g: number): string {
  let d = '';
  for (const p of polys) {
    if (p.length < 6) continue;
    d += `M${n2(p[0]! * g)} ${n2(p[1]! * g)}`;
    for (let i = 2; i < p.length; i += 2) d += `L${n2(p[i]! * g)} ${n2(p[i + 1]! * g)}`;
    d += 'Z';
  }
  return d;
}

export function rectsPath(rects: Bounds[], g: number): string {
  let d = '';
  for (const r of rects) d += `M${n2(r.x * g)} ${n2(r.y * g)}h${n2(r.w * g)}v${n2(r.h * g)}h${n2(-r.w * g)}Z`;
  return d;
}

export function segmentsPath(segs: number[][], g: number): string {
  let d = '';
  for (const [ax, ay, bx, by] of segs) d += `M${n2(ax! * g)} ${n2(ay! * g)}L${n2(bx! * g)} ${n2(by! * g)}`;
  return d;
}
